import FormControl, { FormControlProps } from '@ui-components-3/ui/lib/components/FormControl'
import DateTimePicker, { DateTimePickerProps } from '@ui-components-3/ui/lib/components/DateTimePicker'
import { forwardRef, ReactNode, useCallback, useEffect, useImperativeHandle, useRef } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { format, isValid, parse } from 'date-fns'

type FormDateTimePickerProps = Omit<DateTimePickerProps, 'className' | 'value' | 'onChange'> & {
  name: string
  label?: ReactNode
  className?: string
  formControlProps?: Omit<FormControlProps, 'label' | 'className'>
  inputClassName?: string
}

const FormDateTimePicker = forwardRef<HTMLInputElement, FormDateTimePickerProps>(
  (
    { label, name, className, inputClassName, formControlProps, format: dateFormat = 'MM-dd-yyyy', onBlur, ...props },
    ref,
  ) => {
    const innerRef = useRef<HTMLInputElement>(null)

    const {
      setValue,
      formState: { isSubmitted },
    } = useFormContext()
    const {
      field,
      fieldState: { error, isTouched },
    } = useController({ name })
    const { helper, helperVariant, ...restFormControlProps } = formControlProps || {}
    const showError = !!error?.message && (!!isTouched || !!isSubmitted)

    const { value } = field || {}

    const handleChange = useCallback(
      (date: Date) => {
        setValue(name, date ? format(date, dateFormat) : null)
      },
      [dateFormat, name, setValue],
    )

    const handleInputBlur = useCallback(() => {
      const inputValue = innerRef?.current?.value || ''
      let valueToSet: string | null = inputValue

      if (!isValid(parse(inputValue, dateFormat, new Date()))) {
        valueToSet = null
      }

      setValue(name, valueToSet)
      field.onBlur()
    }, [dateFormat, field, name, setValue])

    const syncValueWithInput = useCallback(() => {
      const inputValue = innerRef?.current?.value
      if (value || inputValue) {
        if (value !== inputValue) {
          // parse date to ensure input value is valid date
          const newValueDate = inputValue ? parse(inputValue, dateFormat, new Date()) : ''

          if (isValid(newValueDate)) {
            const newValue = format(newValueDate, dateFormat)

            if (newValue !== value) {
              setValue(name, newValue)
            }
          }
        }
        field.onBlur()
        onBlur?.()
      }
    }, [dateFormat, field, name, onBlur, setValue, value])

    useEffect(() => {
      const timeout = setTimeout(() => {
        syncValueWithInput()
      }, 10)

      return () => {
        clearTimeout(timeout)
      }
    }, [syncValueWithInput])

    useImperativeHandle(ref, () => innerRef.current!, [])

    return (
      <FormControl
        {...restFormControlProps}
        label={label}
        className={className}
        helper={showError ? error?.message : helper}
        helperVariant={showError ? 'error' : helperVariant}
      >
        <DateTimePicker
          autoComplete="off"
          {...props}
          {...field}
          onChange={handleChange}
          onBlur={handleInputBlur}
          variant={showError ? 'error' : props.variant}
          format={dateFormat}
          className={inputClassName}
          ref={innerRef}
        />
      </FormControl>
    )
  },
)

FormDateTimePicker.displayName = 'FormDateTimePicker'

export default FormDateTimePicker
